import { AuthSession } from '@lumora/common';

// Web-specific session management utilities
export interface WebSessionConfig {
  sessionTimeout: number; // in milliseconds
  refreshThreshold: number; // in milliseconds before expiry to refresh
  storageKey: string;
  rememberMe: boolean;
}

export interface WebSessionData extends AuthSession {
  lastActivity: number;
  deviceInfo: {
    userAgent: string;
    screenResolution: string;
    timezone: string;
  };
  rememberMe: boolean;
  sessionId: string;
  _lastPersist?: number; // Internal tracking for throttling
}

export class WebSessionManager {
  private static instance: WebSessionManager;
  private config: WebSessionConfig;
  private sessionData: WebSessionData | null = null;
  private refreshTimer: NodeJS.Timeout | null = null;
  private activityTimer: NodeJS.Timeout | null = null;

  private constructor(config: Partial<WebSessionConfig> = {}) {
    this.config = {
      sessionTimeout: 24 * 60 * 60 * 1000, // 24 hours default
      refreshThreshold: 5 * 60 * 1000, // 5 minutes before expiry
      storageKey: 'lumora_session',
      rememberMe: false,
      ...config,
    };

    // Set up activity tracking
    this.setupActivityTracking();
  }

  static getInstance(config?: Partial<WebSessionConfig>): WebSessionManager {
    if (!WebSessionManager.instance) {
      WebSessionManager.instance = new WebSessionManager(config);
    }
    return WebSessionManager.instance;
  }

  // Initialize session with auth data
  initializeSession(
    authSession: AuthSession,
    rememberMe: boolean = false
  ): WebSessionData {
    const deviceInfo = this.getDeviceInfo();

    // Reuse existing session ID if we have one for the same user
    let sessionId = this.sessionData?.sessionId;

    // If no existing session ID, or if the user has changed, generate a new one
    if (!sessionId || this.sessionData?.user.id !== authSession.user.id) {
      sessionId = crypto.randomUUID();
    }

    this.sessionData = {
      ...authSession,
      lastActivity: Date.now(),
      deviceInfo,
      rememberMe,
      sessionId,
    };

    // Always persist session for continuity across page refreshes
    this.persistSession();

    // Set up automatic refresh
    this.setupSessionRefresh();

    return this.sessionData;
  }

  // Get current session
  getCurrentSession(): WebSessionData | null {
    // Check if session is expired
    if (this.sessionData && this.isSessionExpired()) {
      this.clearSession();
      return null;
    }

    return this.sessionData;
  }

  // Update session activity
  updateActivity(): void {
    if (this.sessionData) {
      this.sessionData.lastActivity = Date.now();

      // Throttle persistence - only persist every 30 seconds to avoid spam
      const now = Date.now();
      const lastPersist = this.sessionData._lastPersist || 0;
      if (now - lastPersist > 30000) {
        // 30 seconds
        this.sessionData._lastPersist = now;
        this.persistSession();
      }
    }
  }

  // Check if session is expired
  isSessionExpired(): boolean {
    if (!this.sessionData) return true;

    const now = Date.now();
    const sessionAge = now - this.sessionData.lastActivity;
    const tokenExpiry = this.sessionData.expiresAt * 1000; // Convert to milliseconds

    return sessionAge > this.config.sessionTimeout || now > tokenExpiry;
  }

  // Check if session needs refresh
  needsRefresh(): boolean {
    if (!this.sessionData) return false;

    const now = Date.now();
    const tokenExpiry = this.sessionData.expiresAt * 1000;

    return tokenExpiry - now < this.config.refreshThreshold;
  }

  // Update session with new auth data
  updateSession(authSession: AuthSession): void {
    if (this.sessionData) {
      this.sessionData = {
        ...this.sessionData,
        ...authSession,
        lastActivity: Date.now(),
      };

      // Always persist updated session
      this.persistSession();
    }
  }

  // Clear session
  clearSession(): void {
    this.sessionData = null;
    this.clearPersistedSession();
    this.clearTimers();
  }

  // Get device information
  private getDeviceInfo() {
    return {
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      screenResolution:
        typeof screen !== 'undefined' ? `${screen.width}x${screen.height}` : '',
      timezone:
        typeof Intl !== 'undefined'
          ? Intl.DateTimeFormat().resolvedOptions().timeZone
          : '',
    };
  }

  // Set up activity tracking
  private setupActivityTracking(): void {
    if (typeof window === 'undefined') return;

    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
    ];

    const activityHandler = () => {
      this.updateActivity();
    };

    events.forEach(event => {
      window.addEventListener(event, activityHandler, { passive: true });
    });

    // Set up periodic activity check
    this.activityTimer = setInterval(() => {
      if (this.isSessionExpired()) {
        this.clearSession();
        // Emit session expired event
        window.dispatchEvent(new CustomEvent('sessionExpired'));
      }
    }, 60000); // Check every minute
  }

  // Set up automatic session refresh
  private setupSessionRefresh(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    if (!this.sessionData) return;

    const now = Date.now();
    const tokenExpiry = this.sessionData.expiresAt * 1000;
    const refreshTime = tokenExpiry - this.config.refreshThreshold;
    const timeUntilRefresh = Math.max(0, refreshTime - now);

    this.refreshTimer = setTimeout(() => {
      if (this.needsRefresh()) {
        // Emit refresh needed event
        window.dispatchEvent(new CustomEvent('sessionRefreshNeeded'));
      }
    }, timeUntilRefresh);
  }

  // Persist session to storage
  private persistSession(): void {
    if (typeof window === 'undefined' || !this.sessionData) return;

    try {
      const sessionString = JSON.stringify({
        ...this.sessionData,
        // Store tokens in sessionStorage for page refresh persistence
        // Only exclude tokens if we explicitly don't want persistence
      });

      if (this.sessionData.rememberMe) {
        localStorage.setItem(this.config.storageKey, sessionString);
      } else {
        sessionStorage.setItem(this.config.storageKey, sessionString);
      }
    } catch (error) {
      console.error('Failed to persist session:', error);
    }
  }

  // Load session from storage
  loadPersistedSession(): WebSessionData | null {
    if (typeof window === 'undefined') return null;

    try {
      const sessionString =
        localStorage.getItem(this.config.storageKey) ||
        sessionStorage.getItem(this.config.storageKey);

      if (!sessionString) {
        return null;
      }

      const sessionData = JSON.parse(sessionString) as
        | WebSessionData
        | AuthSession;

      // Validate session data
      if (!sessionData.user || !sessionData.expiresAt) {
        this.clearPersistedSession();
        return null;
      }

      // Check if session has valid tokens - if not, it's from old broken storage
      if (!sessionData.accessToken || !sessionData.refreshToken) {
        this.clearPersistedSession();
        return null;
      }

      // Check if persisted session is expired
      if (Date.now() > sessionData.expiresAt * 1000) {
        // If we have a refresh token, we allow returning the expired session
        // so the AuthProvider can attempt to refresh it
        if (!sessionData.refreshToken) {
          this.clearPersistedSession();
          return null;
        }
        // We return the session even if expired, relying on the caller (AuthProvider)
        // to handle the refresh logic
      }

      // Handle compatibility: if it's just an AuthSession, convert to WebSessionData
      if (!('lastActivity' in sessionData)) {
        // It's an AuthSession stored by WebSessionAdapter, convert it
        const deviceInfo = this.getDeviceInfo();
        this.sessionData = {
          ...(sessionData as AuthSession),
          lastActivity: Date.now(),
          deviceInfo,
          rememberMe: this.config.rememberMe, // Use config default
          sessionId: crypto.randomUUID(), // Generate new session ID for legacy sessions
        };
      } else {
        // It's already a WebSessionData
        const webSession = sessionData as WebSessionData;
        if (!webSession.sessionId) {
          webSession.sessionId = crypto.randomUUID();
        }
        this.sessionData = webSession;
      }

      this.setupSessionRefresh();

      return this.sessionData;
    } catch (error) {
      console.error('Failed to load persisted session:', error);
      this.clearPersistedSession();
      return null;
    }
  }

  // Clear persisted session
  private clearPersistedSession(): void {
    if (typeof window === 'undefined') return;

    localStorage.removeItem(this.config.storageKey);
    sessionStorage.removeItem(this.config.storageKey);
  }

  // Clear all timers
  private clearTimers(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    if (this.activityTimer) {
      clearInterval(this.activityTimer);
      this.activityTimer = null;
    }
  }

  // Get session statistics
  getSessionStats() {
    if (!this.sessionData) return null;

    const now = Date.now();
    const sessionAge = now - (this.sessionData.lastActivity || now);
    const tokenExpiry = this.sessionData.expiresAt * 1000;
    const timeUntilExpiry = Math.max(0, tokenExpiry - now);

    return {
      isActive: !this.isSessionExpired(),
      sessionAge: Math.floor(sessionAge / 1000), // in seconds
      timeUntilExpiry: Math.floor(timeUntilExpiry / 1000), // in seconds
      needsRefresh: this.needsRefresh(),
      rememberMe: this.sessionData.rememberMe,
      deviceInfo: this.sessionData.deviceInfo,
    };
  }

  // Cleanup on page unload
  cleanup(): void {
    this.clearTimers();

    // Only clear session storage on logout, not on every page unload
    // The session should persist across page refreshes
  }
}

// Export singleton instance
export const webSessionManager = WebSessionManager.getInstance();

// Set up cleanup on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    webSessionManager.cleanup();
  });
}
